/**
 * useCreateGroupForm — web counterpart of mobile's useCreateGroup: same fields,
 * validation and shared groupService call, with browser File pickers instead of
 * the native media picker.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import groupService from '@wlb/shared/api/services/groupService';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { GROUP_POST_PERMISSION, GROUP_PRIVACY, GROUP_STATUS } from '@wlb/shared/constants';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import type { ApiError } from '@wlb/shared/types/api';
import {
  useGroupImagePicker,
  type GroupImageSlot,
  type PickedGroupImage,
} from './useGroupImagePicker';

interface CreateGroupErrors {
  groupName?: string;
  description?: string;
  privacy?: string;
}

export interface UseCreateGroupFormResult {
  groupName: string;
  description: string;
  privacy: string;
  postPermission: string;
  coverImage: PickedGroupImage | null;
  avatarImage: PickedGroupImage | null;
  errors: CreateGroupErrors;
  loading: boolean;
  setGroupName: (value: string) => void;
  setDescription: (value: string) => void;
  setPrivacy: (value: string) => void;
  setPostPermission: (value: string) => void;
  setImage: (slot: GroupImageSlot, file: File) => void;
  submit: () => Promise<void>;
}

export function useCreateGroupForm(): UseCreateGroupFormResult {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addCreatedGroup, fetchMyGroups } = useGroup();

  const [groupName, setGroupNameValue] = useState('');
  const [description, setDescriptionValue] = useState('');
  const [privacy, setPrivacyValue] = useState<string>(GROUP_PRIVACY.PUBLIC);
  // The posting-permission picker is hidden for now, so every new group starts
  // open to all members; restoring the field is enough to make it selectable.
  const [postPermission, setPostPermissionValue] = useState<string>(
    GROUP_POST_PERMISSION.ALL_MEMBERS,
  );
  const { coverImage, avatarImage, setImage } = useGroupImagePicker();
  const [errors, setErrors] = useState<CreateGroupErrors>({});
  const [loading, setLoading] = useState(false);

  const setGroupName = useCallback((value: string) => {
    setGroupNameValue(value);
    setErrors(prev => (prev.groupName ? { ...prev, groupName: undefined } : prev));
  }, []);

  const setDescription = useCallback((value: string) => {
    setDescriptionValue(value);
    setErrors(prev => (prev.description ? { ...prev, description: undefined } : prev));
  }, []);

  const setPrivacy = useCallback((value: string) => {
    setPrivacyValue(value);
    setErrors(prev => (prev.privacy ? { ...prev, privacy: undefined } : prev));
  }, []);

  const setPostPermission = useCallback((value: string) => {
    setPostPermissionValue(value);
  }, []);

  const submit = useCallback(async () => {
    const nextErrors: CreateGroupErrors = {};
    if (!groupName.trim()) nextErrors.groupName = t('home.createGroupForm.nameRequired');
    if (!description.trim()) nextErrors.description = t('home.createGroupForm.descRequired');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);
    try {
      const response = await groupService.createGroup({
        groupName: groupName.trim(),
        groupDescription: description.trim(),
        privacyType: privacy,
        requireApproval: privacy === GROUP_PRIVACY.PRIVATE,
        requirePostApproval:
          postPermission === GROUP_POST_PERMISSION.ALL_MEMBERS_WITH_APPROVAL,
        onlyAdminCanPost: postPermission === GROUP_POST_PERMISSION.ADMINS_ONLY,
        groupImage: avatarImage?.file ?? null,
        groupCoverImage: coverImage?.file ?? null,
      });

      if (!response?.success) {
        throw new Error('Create group failed');
      }

      if (response.data) {
        addCreatedGroup({
          ...response.data,
          groupName: response.data.groupName || groupName.trim(),
          groupDescription: response.data.groupDescription || description.trim(),
          privacyType: response.data.privacyType || privacy,
          totalMembers: response.data.totalMembers ?? 1,
          status: response.data.status || GROUP_STATUS.OWNER,
        });
      } else {
        await fetchMyGroups(undefined, { force: true });
      }
      navigate('/', { replace: true });
    } catch (err) {
      ToastService.show({
        type: 'error',
        message:
          getApiErrorMessage(err as ApiError) || t('home.createGroupForm.errorMessage'),
      });
    } finally {
      setLoading(false);
    }
  }, [
    groupName,
    description,
    privacy,
    postPermission,
    avatarImage,
    coverImage,
    addCreatedGroup,
    fetchMyGroups,
    navigate,
    t,
  ]);

  return {
    groupName,
    description,
    privacy,
    postPermission,
    coverImage,
    avatarImage,
    errors,
    loading,
    setGroupName,
    setDescription,
    setPrivacy,
    setPostPermission,
    setImage,
    submit,
  };
}
